// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from "./client";
import { client } from "./client.gen";
import type {
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsErrors,
  AdminActivityAnalyticsResponses,
  AdminDeleteUserData,
  AdminDeleteUserErrors,
  AdminDeleteUserResponses,
  AdminGetUserData,
  AdminGetUserErrors,
  AdminGetUserResponses,
  AdminListActivityData,
  AdminListActivityErrors,
  AdminListActivityResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminUpdateUserData,
  AdminUpdateUserErrors,
  AdminUpdateUserResponses,
  ChangePasswordData,
  ChangePasswordErrors,
  ChangePasswordResponses,
  GetActivityData,
  GetActivityResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeDetailData,
  GetMeDetailResponses,
  GetMeResponses,
  LoginData,
  LoginErrors,
  LoginResponses,
  LogoutData,
  LogoutResponses,
  PlannerDeleteDocData,
  PlannerDeleteDocErrors,
  PlannerDeleteDocResponses,
  PlannerListDocsData,
  PlannerListDocsErrors,
  PlannerListDocsResponses,
  PlannerSetDocData,
  PlannerSetDocErrors,
  PlannerSetDocResponses,
  UpdateProfileData,
  UpdateProfileErrors,
  UpdateProfileResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Login
 *
 * Authenticate via Supabase, then set the signed session cookie.
 *
 * The Supabase access/refresh tokens never leave the backend — the browser
 * only ever sees the HMAC cookie this endpoint sets.
 */
export const login = <ThrowOnError extends boolean = false>(
  options: Options<LoginData, ThrowOnError>,
) =>
  (options.client ?? client).post<LoginResponses, LoginErrors, ThrowOnError>({
    url: "/auth/login",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Logout
 *
 * Sign out — clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Me
 *
 * Get the current user's profile.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });

/**
 * Auth Me Detail
 *
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });

/**
 * Auth Update Profile
 *
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
) =>
  (options.client ?? client).put<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Change Password
 *
 * Change the current user's password (verifies current password first).
 */
export const changePassword = <ThrowOnError extends boolean = false>(
  options: Options<ChangePasswordData, ThrowOnError>,
) =>
  (options.client ?? client).put<ChangePasswordResponses, ChangePasswordErrors, ThrowOnError>({
    url: "/auth/password",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Activity
 *
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });

/**
 * Admin List Users
 *
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });

/**
 * Admin Delete User
 *
 * Delete a user (admin only). Self-deletion blocked.
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
) =>
  (options.client ?? client).delete<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Get User
 *
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
) =>
  (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Update User
 *
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
) =>
  (options.client ?? client).put<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Activity
 *
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the list_recent_activity RPC — the client sends params and renders results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({ url: "/admin/activity", ...options });

/**
 * Admin Activity Analytics
 *
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/activity/analytics", ...options });

/**
 * Admin Activity Actions
 *
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });

/**
 * Planner List Docs
 *
 * List every document in a planner collection.
 */
export const plannerListDocs = <ThrowOnError extends boolean = false>(
  options: Options<PlannerListDocsData, ThrowOnError>,
) =>
  (options.client ?? client).get<PlannerListDocsResponses, PlannerListDocsErrors, ThrowOnError>({
    url: "/planner/db/{collection}",
    ...options,
  });

/**
 * Planner Delete Doc
 *
 * Delete a planner document.
 */
export const plannerDeleteDoc = <ThrowOnError extends boolean = false>(
  options: Options<PlannerDeleteDocData, ThrowOnError>,
) =>
  (options.client ?? client).delete<
    PlannerDeleteDocResponses,
    PlannerDeleteDocErrors,
    ThrowOnError
  >({ url: "/planner/db/{collection}/{doc_id}", ...options });

/**
 * Planner Set Doc
 *
 * Create or replace a planner document.
 */
export const plannerSetDoc = <ThrowOnError extends boolean = false>(
  options: Options<PlannerSetDocData, ThrowOnError>,
) =>
  (options.client ?? client).put<PlannerSetDocResponses, PlannerSetDocErrors, ThrowOnError>({
    url: "/planner/db/{collection}/{doc_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
